export const ASSESSMENT_MANIFEST = {
  version: "assessment-v1",
  tier_certainty: { E1: "high", E2: "high", E3: "medium", E4: "low", E5: "speculative" } as const,
  certainty_order: ["high", "medium", "low", "speculative"] as const,
  certainty_weight: { high: 1.0, medium: 0.7, low: 0.4, speculative: 0.2 } as const,
  effort_rank: ["S", "M", "L", "XL"] as const,
  band_factor: { 1: 1.0, 2: 1.25, 3: 1.6, 4: 2.0, 5: 2.5 } as const,
  default_ramp_months: 10.5,
};

export type Tier = keyof typeof ASSESSMENT_MANIFEST.tier_certainty;
export type Certainty = (typeof ASSESSMENT_MANIFEST.certainty_order)[number];
export type Effort = (typeof ASSESSMENT_MANIFEST.effort_rank)[number];

export const TIER_LABELS: Record<Tier, string> = {
  E1: "E1 · comparable live partner actuals (high certainty)",
  E2: "E2 · partner-stated figures with a document (high certainty)",
  E3: "E3 · partner-stated figures, verbal only (medium certainty)",
  E4: "E4 · market comparables, no partner input (low certainty)",
  E5: "E5 · operator judgement, no external evidence (speculative)",
};
export const EFFORT_LABELS: Record<Effort, string> = { S: "S · under 10 h", M: "M · 10 to 40 h", L: "L · 40 to 120 h", XL: "XL · over 120 h" };
export const DIFFICULTY_LABELS: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: "1 · one contact, they want this", 2: "2 · one decision maker to convince", 3: "3 · several stakeholders",
  4: "4 · procurement or listing process", 5: "5 · regulatory or exclusivity hurdle",
};

export function allowedCertainties(tier: Tier): Certainty[] {
  const base = ASSESSMENT_MANIFEST.tier_certainty[tier];
  const index = ASSESSMENT_MANIFEST.certainty_order.indexOf(base);
  return index + 1 < ASSESSMENT_MANIFEST.certainty_order.length ? [base, ASSESSMENT_MANIFEST.certainty_order[index + 1]] : [base];
}
export function yearFromMonth(base: number, rampOverride: number | null): number {
  return Math.round((rampOverride ?? base * ASSESSMENT_MANIFEST.default_ramp_months) * 100) / 100;
}
export function band(difficulty: 1 | 2 | 3 | 4 | 5, effort: Effort): 1 | 2 | 3 | 4 | 5 {
  const half = (difficulty + ASSESSMENT_MANIFEST.effort_rank.indexOf(effort) + 1) / 2;
  const floor = Math.floor(half);
  const rounded = half - floor === 0.5 ? (floor % 2 === 0 ? floor : floor + 1) : Math.round(half);
  return Math.max(1, Math.min(5, rounded)) as 1 | 2 | 3 | 4 | 5;
}
export function priority(base: number, certainty: Certainty, difficulty: 1 | 2 | 3 | 4 | 5, effort: Effort): number {
  return Math.round((base * ASSESSMENT_MANIFEST.certainty_weight[certainty] / ASSESSMENT_MANIFEST.band_factor[band(difficulty, effort)]) * 10000) / 10000;
}
export function previewLine(base: number, tier: Tier, certainty: Certainty, difficulty: 1 | 2 | 3 | 4 | 5, effort: Effort): string {
  const label = certainty.charAt(0).toUpperCase() + certainty.slice(1);
  return `Priority ${priority(base, certainty, difficulty, effort)} - ${base} × ${ASSESSMENT_MANIFEST.certainty_weight[certainty]} (${tier} ${label}) ÷ ${ASSESSMENT_MANIFEST.band_factor[band(difficulty, effort)]} (difficulty ${difficulty} · effort ${effort})`;
}
export function previewRank(score: number, leads: Array<{ priority_score: number | null; node_id?: string | null }>, nodeId: string | null): { branch: number; overall: number } {
  const assessed = leads.filter((lead) => lead.priority_score !== null);
  return {
    overall: 1 + assessed.filter((lead) => (lead.priority_score as number) > score).length,
    branch: 1 + assessed.filter((lead) => lead.node_id === nodeId && (lead.priority_score as number) > score).length,
  };
}
